import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCallback, useState, useEffect } from "react";
import { Radio, Loader2, Copy, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  startStreamingMeetingApi,
  stopStreamingMeetingApi,
  getStreamingsMeetingApi,
  updateStreamingLayoutApi,
} from "@/modules/meetings/api/api-meeting";
import {
  Meeting,
  StreamInfo,
} from "@/modules/meetings/types/meeting.type";

interface StreamSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meeting: Meeting | null;
  isHost: boolean;
}

export const StreamSheet: React.FC<StreamSheetProps> = ({
  open,
  onOpenChange,
  meeting,
  isHost,
}) => {
  
  // Streaming states
  const [isStreaming, setIsStreaming] = useState(false);
  const [isStreamingLoading, setIsStreamingLoading] = useState(false);
  const [activeStream, setActiveStream] = useState<{
    egressId: string;
    startedAt: string;
    status: string;
  } | null>(null);
  const [streamingDuration, setStreamingDuration] = useState<number>(0);
  const [streams, setStreams] = useState<StreamInfo[]>([]);
  const [isLoadingStreams, setIsLoadingStreams] = useState(false);
  const [stoppingIds, setStoppingIds] = useState<Set<string>>(new Set());
  const [updatingLayoutIds, setUpdatingLayoutIds] = useState<Set<string>>(new Set());
  const [currentLayout, setCurrentLayout] = useState<string>("grid");

  // Stream form states
  const [streamUrlsText, setStreamUrlsText] = useState<string>(
    meeting?.roomMeeting?.linkStream + "/" + meeting?.roomMeeting?.keyStream || ""
  );
  const [layout, setLayout] = useState<string>("grid");
  const [audioMixing, setAudioMixing] = useState<string>("DEFAULT_MIXING");
  const [encodingPreset, setEncodingPreset] = useState<string>("");
  const [customBaseUrl, setCustomBaseUrl] = useState<string>("");
  const [audioOnly, setAudioOnly] = useState<boolean>(false);
  const [videoOnly, setVideoOnly] = useState<boolean>(false);
  const [showCustomEncoding, setShowCustomEncoding] = useState<boolean>(false);
  const [customEncoding, setCustomEncoding] = useState<{
    width?: number;
    height?: number;
    bitrate?: number;
    frameRate?: number;
    videoBitrate?: number;
    audioBitrate?: number;
    audioSampleRate?: number;
    audioFrequency?: number;
    keyFrameInterval?: number;
  }>({});
  const [showStreamForm, setShowStreamForm] = useState(false);
useEffect(() => {
  if (meeting?.roomMeeting?.linkStream && meeting?.roomMeeting?.keyStream) {
    setStreamUrlsText(meeting?.roomMeeting?.linkStream + "/" + meeting?.roomMeeting?.keyStream);
  }
}, [meeting?.roomMeeting?.linkStream, meeting?.roomMeeting?.keyStream,showStreamForm]);
  // Format thời gian (HH:MM:SS)
  const formatDuration = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Fetch streams
  const fetchStreams = useCallback(async () => {
    if (!meeting?.id) return;

    try {
      setIsLoadingStreams(true);
      const response = await getStreamingsMeetingApi(meeting.id);

      let streamsData: StreamInfo[] = [];
      if (response?.data?.data) {
        if (Array.isArray(response.data.data)) {
          streamsData = response.data.data;
        } else if (
          response.data.data.streams &&
          Array.isArray(response.data.data.streams)
        ) {
          streamsData = response.data.data.streams;
        }
      } else if (Array.isArray(response?.data)) {
        streamsData = response.data;
      }

      setStreams(streamsData);

      // Tìm stream đang chạy
      const active = streamsData.find(
        (stream) =>
          stream.status === "EGRESS_STARTING" ||
          stream.status === "EGRESS_ACTIVE"
      );

      if (active && active.startedAt) {
        setActiveStream({
          egressId: active.egressId,
          startedAt: active.startedAt,
          status: active.status || "active",
        });
        setIsStreaming(true);
        if (active.layout) {
          setCurrentLayout(active.layout);
        }
      } else {
        setActiveStream(null);
        if (!isStreamingLoading) {
          setIsStreaming(false);
        }
      }
    } catch (error) {
      console.error("Error fetching streams:", error);
    } finally {
      setIsLoadingStreams(false);
    }
  }, [meeting?.id, isStreamingLoading]);

  // Tính toán thời gian đã stream
  useEffect(() => {
    if (!activeStream?.startedAt) {
      setStreamingDuration(0);
      return;
    }

    const updateDuration = () => {
      const startTime = new Date(activeStream.startedAt).getTime();
      const now = Date.now();
      const duration = Math.floor((now - startTime) / 1000);
      setStreamingDuration(duration);
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [activeStream?.startedAt]);

  // Fetch khi sheet mở
  useEffect(() => {
    if (open && meeting?.id) {
      fetchStreams();
    }
  }, [open, meeting?.id, fetchStreams]);

  // Handle start stream
  const handleStartStream = useCallback(
    async (
      streamUrls: string[],
      layout: string,
      audioMixing: string,
      encodingPreset?: string,
      customEncoding?: {
        width?: number;
        height?: number;
        bitrate?: number;
        frameRate?: number;
        videoBitrate?: number;
        audioBitrate?: number;
        audioSampleRate?: number;
        audioFrequency?: number;
        keyFrameInterval?: number;
      },
      audioOnly?: boolean,
      videoOnly?: boolean,
      customBaseUrl?: string
    ) => {
      if (!meeting || !meeting.id) {
        toast.error("Không tìm thấy thông tin cuộc họp");
        return;
      }

      if (streamUrls.length === 0) {
        toast.error("Vui lòng nhập ít nhất một RTMP URL");
        return;
      }

      try {
        setIsStreamingLoading(true);

        const payload: {
          streamUrls: string[];
          layout?: string;
          encodingPreset?: string;
          customEncoding?: {
            width?: number;
            height?: number;
            bitrate?: number;
            frameRate?: number;
            videoBitrate?: number;
            audioBitrate?: number;
            audioSampleRate?: number;
            audioFrequency?: number;
            keyFrameInterval?: number;
          };
          audioOnly?: boolean;
          videoOnly?: boolean;
          audioMixing?: string;
          customBaseUrl?: string;
        } = {
          streamUrls,
        };

        if (layout) payload.layout = layout;
        if (encodingPreset && encodingPreset !== "none") payload.encodingPreset = encodingPreset;
        if (customEncoding && Object.keys(customEncoding).length > 0) {
          payload.customEncoding = customEncoding;
        }
        if (audioOnly) payload.audioOnly = audioOnly;
        if (videoOnly) payload.videoOnly = videoOnly;
        if (audioMixing) payload.audioMixing = audioMixing;
        if (customBaseUrl) payload.customBaseUrl = customBaseUrl;

        await startStreamingMeetingApi(meeting.id, payload);

        setIsStreaming(true);
        setShowStreamForm(false);
        setStreamUrlsText("");
        setEncodingPreset("");
        setCustomBaseUrl("");
        setAudioOnly(false);
        setVideoOnly(false);
        setShowCustomEncoding(false);
        setCustomEncoding({});
        toast.success(
          `Đã bắt đầu stream meeting đến ${streamUrls.length} destination${
            streamUrls.length > 1 ? "s" : ""
          }`
        );
        setTimeout(() => {
          fetchStreams();
        }, 1000);
      } catch (error: unknown) {
        console.error("Error starting stream:", error);
        const errorMessage =
          (error as { data?: { error?: { message?: string } } })?.data?.error
            ?.message || "Đã xảy ra lỗi khi bắt đầu stream";
        toast.error(errorMessage);
        throw error;
      } finally {
        setIsStreamingLoading(false);
      }
    },
    [meeting, fetchStreams]
  );

  // Handle stop stream
  const handleStopStream = useCallback(
    async (egressId: string) => {
      if (!meeting?.id) return;

      try {
        setStoppingIds((prev) => new Set(prev).add(egressId));
        await stopStreamingMeetingApi(meeting.id, { egressId });
        toast.success("Đã dừng stream");
        await fetchStreams();
      } catch (error: unknown) {
        console.error("Error stopping stream:", error);
        const errorMessage =
          (error as { data?: { error?: { message?: string } } })?.data?.error
            ?.message || "Đã xảy ra lỗi khi dừng stream";
        toast.error(errorMessage);
      } finally {
        setStoppingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(egressId);
          return newSet;
        });
      }
    },
    [meeting?.id, fetchStreams]
  );

  // Handle update streaming layout
  const handleUpdateStreamingLayout = useCallback(
    async (egressId: string, newLayout: string) => {
      if (!meeting?.id) return;

      try {
        setUpdatingLayoutIds((prev) => new Set(prev).add(egressId));
        await updateStreamingLayoutApi(meeting.id, {
          egressId,
          layout: newLayout,
        });
        setCurrentLayout(newLayout);
        toast.success("Đã cập nhật bố cục");
        await fetchStreams();
      } catch (error: unknown) {
        console.error("Error updating streaming layout:", error);
        const errorMessage =
          (error as { data?: { error?: { message?: string } } })?.data?.error
            ?.message || "Đã xảy ra lỗi khi cập nhật bố cục";
        toast.error(errorMessage);
      } finally {
        setUpdatingLayoutIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(egressId);
          return newSet;
        });
      }
    },
    [meeting?.id, fetchStreams]
  );

  // Handle submit stream form
  const handleSubmitStream = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const streamUrls = streamUrlsText
        .split("\n")
        .map((url) => url.trim())
        .filter((url) => url.length > 0);

      if (streamUrls.length === 0) {
        return;
      }

      const customEnc = showCustomEncoding && Object.keys(customEncoding).length > 0
        ? {
            ...(customEncoding.width && { width: Number(customEncoding.width) }),
            ...(customEncoding.height && { height: Number(customEncoding.height) }),
            ...(customEncoding.bitrate && { bitrate: Number(customEncoding.bitrate) }),
            ...(customEncoding.frameRate && { frameRate: Number(customEncoding.frameRate) }),
            ...(customEncoding.videoBitrate && { videoBitrate: Number(customEncoding.videoBitrate) }),
            ...(customEncoding.audioBitrate && { audioBitrate: Number(customEncoding.audioBitrate) }),
            ...(customEncoding.audioSampleRate && { audioSampleRate: Number(customEncoding.audioSampleRate) }),
            ...(customEncoding.audioFrequency && { audioFrequency: Number(customEncoding.audioFrequency) }),
            ...(customEncoding.keyFrameInterval && { keyFrameInterval: Number(customEncoding.keyFrameInterval) }),
          }
        : undefined;

      await handleStartStream(
        streamUrls,
        layout,
        audioMixing,
        encodingPreset || undefined,
        customEnc,
        audioOnly,
        videoOnly,
        customBaseUrl || undefined
      );
    },
    [
      streamUrlsText,
      layout,
      audioMixing,
      encodingPreset,
      customEncoding,
      showCustomEncoding,
      audioOnly,
      videoOnly,
      customBaseUrl,
      handleStartStream,
    ]
  );

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Đã copy vào clipboard");
  }, []);

  if (!isHost) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Stream cuộc họp</SheetTitle>
          <SheetDescription>Quản lý stream meeting đến các nền tảng</SheetDescription>
        </SheetHeader>

        <div className="space-y-6 p-4">
          {/* Streaming Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Radio className="size-5" />
                Stream
              </h3>
              {isStreaming && (
                <span className="text-sm font-medium text-blue-600">
                  {formatDuration(streamingDuration)}
                </span>
              )}
            </div>

            <div className="space-y-3">
              {!isStreaming && !showStreamForm && (
                <Button
                  variant="default"
                  onClick={() => setShowStreamForm(true)}
                  className="w-full"
                  aria-label="Bắt đầu stream"
                >
                  <Radio className="size-4 mr-2" />
                  Bắt đầu stream
                </Button>
              )}

              {showStreamForm && !isStreaming && (
                <form onSubmit={handleSubmitStream} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="streamUrls">
                      RTMP URLs <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="streamUrls"
                      value={streamUrlsText}
                      onChange={(e) => setStreamUrlsText(e.target.value)}
                      placeholder="rtmp://a.rtmp.youtube.com/live2/YOUR-STREAM-KEY&#10;rtmps://live-api-s.facebook.com:443/rtmp/YOUR-STREAM-KEY"
                      required
                      disabled={isStreamingLoading}
                      rows={4}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Nhập nhiều RTMP URLs, mỗi URL một dòng
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="layout">Bố cục video</Label>
                    <Select
                      value={layout}
                      onValueChange={setLayout}
                      disabled={isStreamingLoading}
                    >
                      <SelectTrigger id="layout">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="speaker">Người thuyết trình</SelectItem>
                        <SelectItem value="grid">Dạng lưới</SelectItem>
                        <SelectItem value="single-speaker">
                          Một người thuyết trình
                        </SelectItem>
                        <SelectItem value="speaker-dark">
                          Người thuyết trình (nền tối)
                        </SelectItem>
                        <SelectItem value="grid-dark">Dạng lưới (nền tối)</SelectItem>
                        <SelectItem value="single-speaker-dark">
                          Một người thuyết trình (nền tối)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="audioMixing">Audio Mixing</Label>
                    <Select
                      value={audioMixing}
                      onValueChange={setAudioMixing}
                      disabled={isStreamingLoading}
                    >
                      <SelectTrigger id="audioMixing">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DEFAULT_MIXING">
                          Default Mixing
                        </SelectItem>
                        <SelectItem value="MICROPHONE_ONLY">
                          Microphone Only
                        </SelectItem>
                        <SelectItem value="SPEAKER_ONLY">
                          Speaker Only
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="encodingPreset">Encoding Preset</Label>
                    <Select
                      value={encodingPreset}
                      onValueChange={setEncodingPreset}
                      disabled={isStreamingLoading}
                    >
                      <SelectTrigger id="encodingPreset">
                        <SelectValue placeholder="Chọn preset (tùy chọn)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Không sử dụng preset</SelectItem>
                        <SelectItem value="H264_720P_30">H264 720P 30fps</SelectItem>
                        <SelectItem value="H264_1080P_30">H264 1080P 30fps</SelectItem>
                        <SelectItem value="H264_1080P_60">H264 1080P 60fps</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="customBaseUrl">Custom Base URL</Label>
                    <Input
                      id="customBaseUrl"
                      type="text"
                      value={customBaseUrl}
                      onChange={(e) => setCustomBaseUrl(e.target.value)}
                      placeholder="https://example.com (tùy chọn)"
                      disabled={isStreamingLoading}
                    />
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="audioOnly"
                        checked={audioOnly}
                        onCheckedChange={(checked) => {
                          setAudioOnly(checked === true);
                          if (checked) setVideoOnly(false);
                        }}
                        disabled={isStreamingLoading}
                      />
                      <Label
                        htmlFor="audioOnly"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Chỉ audio
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="videoOnly"
                        checked={videoOnly}
                        onCheckedChange={(checked) => {
                          setVideoOnly(checked === true);
                          if (checked) setAudioOnly(false);
                        }}
                        disabled={isStreamingLoading}
                      />
                      <Label
                        htmlFor="videoOnly"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Chỉ video
                      </Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setShowCustomEncoding(!showCustomEncoding)}
                      className="flex items-center justify-between w-full p-2 text-sm font-medium text-left border rounded-lg hover:bg-accent"
                      disabled={isStreamingLoading}
                    >
                      <span>Custom Encoding (Tùy chọn)</span>
                      {showCustomEncoding ? (
                        <ChevronUp className="size-4" />
                      ) : (
                        <ChevronDown className="size-4" />
                      )}
                    </button>
                    {showCustomEncoding && (
                      <div className="space-y-3 p-3 border rounded-lg bg-muted/50">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="width" className="text-xs">
                              Width
                            </Label>
                            <Input
                              id="width"
                              type="number"
                              value={customEncoding.width || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  width: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="1920"
                              disabled={isStreamingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="height" className="text-xs">
                              Height
                            </Label>
                            <Input
                              id="height"
                              type="number"
                              value={customEncoding.height || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  height: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="1080"
                              disabled={isStreamingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="frameRate" className="text-xs">
                              Frame Rate
                            </Label>
                            <Input
                              id="frameRate"
                              type="number"
                              value={customEncoding.frameRate || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  frameRate: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="30"
                              disabled={isStreamingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="bitrate" className="text-xs">
                              Bitrate (kbps)
                            </Label>
                            <Input
                              id="bitrate"
                              type="number"
                              value={customEncoding.bitrate || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  bitrate: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="2500"
                              disabled={isStreamingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="videoBitrate" className="text-xs">
                              Video Bitrate (kbps)
                            </Label>
                            <Input
                              id="videoBitrate"
                              type="number"
                              value={customEncoding.videoBitrate || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  videoBitrate: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="2000"
                              disabled={isStreamingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="audioBitrate" className="text-xs">
                              Audio Bitrate (kbps)
                            </Label>
                            <Input
                              id="audioBitrate"
                              type="number"
                              value={customEncoding.audioBitrate || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  audioBitrate: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="128"
                              disabled={isStreamingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="audioSampleRate" className="text-xs">
                              Audio Sample Rate (Hz)
                            </Label>
                            <Input
                              id="audioSampleRate"
                              type="number"
                              value={customEncoding.audioSampleRate || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  audioSampleRate: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="48000"
                              disabled={isStreamingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="audioFrequency" className="text-xs">
                              Audio Frequency (Hz)
                            </Label>
                            <Input
                              id="audioFrequency"
                              type="number"
                              value={customEncoding.audioFrequency || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  audioFrequency: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="48000"
                              disabled={isStreamingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2 col-span-2">
                            <Label htmlFor="keyFrameInterval" className="text-xs">
                              Key Frame Interval
                            </Label>
                            <Input
                              id="keyFrameInterval"
                              type="number"
                              value={customEncoding.keyFrameInterval || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  keyFrameInterval: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="2"
                              disabled={isStreamingLoading}
                              className="h-8"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowStreamForm(false);
                        setStreamUrlsText("");
                      }}
                      disabled={isStreamingLoading}
                      className="flex-1"
                    >
                      Hủy
                    </Button>
                    <Button
                      type="submit"
                      disabled={!streamUrlsText.trim() || isStreamingLoading}
                      className="flex-1"
                    >
                      {isStreamingLoading ? (
                        <>
                          <Loader2 className="size-4 mr-2 animate-spin" />
                          Đang bắt đầu...
                        </>
                      ) : (
                        "Bắt đầu stream"
                      )}
                    </Button>
                  </div>
                </form>
              )}

              {isStreaming && activeStream?.egressId && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="updateLayout">Cập nhật bố cục</Label>
                    <Select
                      value={currentLayout}
                      onValueChange={(value) =>
                        handleUpdateStreamingLayout(activeStream.egressId, value)
                      }
                      disabled={updatingLayoutIds.has(activeStream.egressId)}
                    >
                      <SelectTrigger id="updateLayout">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="speaker">Người thuyết trình</SelectItem>
                        <SelectItem value="grid">Dạng lưới</SelectItem>
                        <SelectItem value="single-speaker">
                          Một người thuyết trình
                        </SelectItem>
                        <SelectItem value="speaker-dark">
                          Người thuyết trình (nền tối)
                        </SelectItem>
                        <SelectItem value="grid-dark">Dạng lưới (nền tối)</SelectItem>
                        <SelectItem value="single-speaker-dark">
                          Một người thuyết trình (nền tối)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => handleStopStream(activeStream.egressId)}
                    disabled={stoppingIds.has(activeStream.egressId)}
                    className="w-full"
                    aria-label="Dừng stream"
                  >
                    {stoppingIds.has(activeStream.egressId) ? (
                      <Loader2 className="size-4 mr-2 animate-spin" />
                    ) : (
                      <Radio className="size-4 mr-2" />
                    )}
                    Dừng stream
                  </Button>
                </div>
              )}

              {isLoadingStreams ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              ) : streams.length > 0 ? (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Danh sách streams ({streams.length})
                  </Label>
                  <div className="space-y-2 overflow-y-auto">
                    {streams.map((stream) => {
                      const isActive =
                        stream.egressId === activeStream?.egressId;
                      return (
                        <div
                          key={stream.egressId}
                          className={cn(
                            "p-3 border rounded-lg text-sm",
                            isActive &&
                              "bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800"
                          )}
                        >
                          <div className="gap-2">
                            <div className="flex items-center justify-between gap-2 shrink-0">
                              <p className="font-medium truncate">
                                {stream.egressId.slice(0, 16)}...
                              </p>
                              <div>
                                {stream.status && (
                                  <span
                                    className={cn(
                                      "text-xs px-2 py-0.5 rounded-full",
                                      isActive
                                        ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                        : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                                    )}
                                  >
                                    {stream.status}
                                  </span>
                                )}
                             
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              {stream.startedAt && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {new Date(stream.startedAt).toLocaleString(
                                    "vi-VN"
                                  )}
                                </p>
                              )}
                              {isActive && (
                                <p className="text-xs text-blue-600 mt-1">
                                  Đang stream:{" "}
                                  {formatDuration(streamingDuration)}
                                </p>
                              )}

                              {(stream.streamUrls as string[]).length > 0 && (
                                <div className="text-xs text-muted-foreground font-mono truncate flex flex-wrap gap-1 border-t pt-1 mt-4 w-full">
                                  {(stream.streamUrls as string[]).map(
                                    (url: string) => (
                                      <div
                                        key={url}
                                        className="flex items-center justify-between gap-2 w-full"
                                      >
                                        <p
                                          className="text-xs text-muted-foreground font-mono truncate flex-1"
                                          title={url}
                                        >
                                          {url}
                                        </p>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => copyToClipboard(url)}
                                          className="h-6 w-6 p-0 shrink-0"
                                          aria-label="Copy URL"
                                        >
                                          <Copy className="size-3" />
                                        </Button>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

